import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, RotateCcw, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { Button } from './ui/button';
import ResponsiveImage from './ResponsiveImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step-by-step quiz: one question per card, multi-select, animated transitions.
// Reads question config from `/api/quiz-config` (admin-editable).
const QuizSection = () => {
  const [config, setConfig] = useState({ headline: '', eyebrow: '', questions: [] });
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState({}); // { [qId]: Set<idx> }
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [pr, cr, qr] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/quiz-config`),
        ]);
        setProducts(pr.data);
        setCats(cr.data);
        setConfig(qr.data || { headline: '', eyebrow: '', questions: [] });
      } catch { /* ignore */ }
    })();
  }, []);

  const QUIZ = config.questions || [];
  const totalSteps = QUIZ.length;
  const isResults = step >= totalSteps;

  const toggle = (qid, idx) => {
    const s = new Set(picks[qid] || []);
    if (s.has(idx)) s.delete(idx); else s.add(idx);
    setPicks({ ...picks, [qid]: s });
  };
  const isPicked = (qid, idx) => (picks[qid] || new Set()).has(idx);
  const hasAnyOnCurrent = step < totalSteps && (picks[QUIZ[step]?.id] || new Set()).size > 0;

  const reset = () => { setPicks({}); setStep(0); };
  const next = () => setStep((s) => Math.min(s + 1, totalSteps));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  // Aggregate selected tags + category slugs
  const { selectedTags, selectedCatSlugs } = useMemo(() => {
    const tags = new Set();
    const catSlugs = new Set();
    QUIZ.forEach((q) => {
      (picks[q.id] || new Set()).forEach((idx) => {
        const a = q.answers[idx];
        if (!a) return;
        (a.tags || []).forEach((t) => tags.add(t));
        const cs = a.category_slug || a.category;
        if (cs) catSlugs.add(cs);
      });
    });
    return { selectedTags: Array.from(tags), selectedCatSlugs: Array.from(catSlugs) };
  }, [picks, QUIZ]);

  const results = useMemo(() => {
    let list = [...products];
    if (selectedCatSlugs.length > 0) {
      const ids = cats.filter((c) => selectedCatSlugs.includes(c.slug)).map((c) => c.id);
      list = list.filter((p) =>
        (p.categories || []).some((id) => ids.includes(id)) ||
        selectedCatSlugs.some((sl) => (p.category || '').toLowerCase().includes(sl))
      );
    }
    if (selectedTags.length > 0) {
      const scored = list.map((p) => {
        const t = (p.tags || []).map((x) => x.toLowerCase());
        const overlap = selectedTags.filter((tag) => t.includes(tag.toLowerCase())).length;
        return { p, score: overlap };
      });
      const matches = scored.filter((s) => s.score > 0);
      if (matches.length > 0) {
        list = matches.sort((a, b) => b.score - a.score).map((s) => s.p);
      }
    }
    return list.slice(0, 8);
  }, [products, selectedCatSlugs, selectedTags, cats]);

  if (QUIZ.length === 0) return <section data-testid="quiz-section" />;

  return (
    <section className="py-10 md:py-16" data-testid="quiz-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Eyebrow + Headline */}
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-1.5 text-yellow-700 text-[11px] font-bold uppercase tracking-widest bg-yellow-100 px-3 py-1 rounded-full">
            <Sparkles className="h-3.5 w-3.5" /> {config.eyebrow || 'Intelligente Suche'}
          </span>
          <h2 className="text-xl md:text-2xl font-bold text-sky-700 mt-2">
            {config.headline || 'Finde dein passendes Modul'}
          </h2>
        </div>

        {/* Progress dots */}
        {!isResults && (
          <div className="flex justify-center items-center gap-1.5 mb-4" aria-hidden>
            {QUIZ.map((_, i) => (
              <span
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i < step ? 'bg-sky-500 w-2'
                  : i === step ? 'bg-sky-600 w-8'
                  : 'bg-sky-200 w-2'
                }`}
              />
            ))}
          </div>
        )}

        {/* Card – wide & short */}
        <div className="relative">
          <div
            key={isResults ? 'res' : step}
            className="bg-white border border-sky-100 rounded-2xl shadow-md p-5 md:p-6 animate-fadeIn"
          >
            {!isResults ? (
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                {/* Left: question */}
                <div className="md:w-56 md:flex-shrink-0 text-center md:text-left">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-sky-500">
                    Frage {step + 1} / {totalSteps}
                  </p>
                  <h3 className="text-lg md:text-xl font-bold text-sky-900 mt-1 leading-snug">
                    {QUIZ[step].question}
                  </h3>
                  <p className="text-[11px] text-neutral-500 mt-0.5">Mehrfachauswahl möglich</p>
                </div>

                {/* Middle: answer pills row */}
                <div className="flex-1 flex flex-wrap gap-2 justify-center md:justify-start">
                  {QUIZ[step].answers.map((a, idx) => {
                    const active = isPicked(QUIZ[step].id, idx);
                    return (
                      <button
                        key={idx}
                        onClick={() => toggle(QUIZ[step].id, idx)}
                        data-testid={`quiz-pick-${QUIZ[step].id}-${idx}`}
                        className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 text-sm font-medium transition group ${
                          active
                            ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                            : 'bg-sky-50 text-sky-900 border-sky-100 hover:border-sky-400 hover:bg-sky-100'
                        }`}
                      >
                        <span className={`flex-shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition ${
                          active ? 'bg-white border-white' : 'border-sky-300 group-hover:border-sky-500'
                        }`}>
                          {active && <Check className="h-3 w-3 text-sky-600" />}
                        </span>
                        {a.label}
                      </button>
                    );
                  })}
                </div>

                {/* Right: nav buttons */}
                <div className="md:flex-shrink-0 flex items-center justify-between md:justify-end gap-2 border-t md:border-t-0 md:border-l border-sky-50 pt-3 md:pt-0 md:pl-4">
                  <Button
                    variant="outline" size="sm"
                    onClick={prev}
                    disabled={step === 0}
                    className="rounded-full disabled:opacity-40"
                    data-testid="quiz-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={next}
                    disabled={!hasAnyOnCurrent}
                    size="sm"
                    className="rounded-full bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50"
                    data-testid="quiz-next"
                  >
                    {step === totalSteps - 1 ? 'Treffer' : 'Weiter'}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            ) : (
              /* Results */
              <div>
                <div className="text-center mb-4">
                  <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
                    Fertig
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-sky-900 mt-2">
                    {results.length > 0
                      ? `Hier sind deine Top-Treffer (${results.length})`
                      : 'Schau dir gerne unsere komplette Auswahl an'}
                  </h3>
                </div>
                {results.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {results.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => navigate(`/${p.slug || p.id}`)}
                        className="group bg-white border border-sky-100 rounded-xl overflow-hidden text-left hover:shadow-md transition"
                        data-testid={`quiz-result-${p.id}`}
                      >
                        <div className="aspect-square bg-sky-100 overflow-hidden">
                          {p.image_url && (
                            <ResponsiveImage
                              src={p.image_url}
                              alt={p.name}
                              sizes="(min-width: 1024px) 22vw, 45vw"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          )}
                        </div>
                        <div className="p-2.5">
                          <p className="font-semibold text-sky-900 text-xs md:text-sm leading-tight">{p.name}</p>
                          <p className="text-[11px] text-sky-700 mt-0.5">{p.price_on_request ? 'Preis auf Anfrage' : `ab €${(p.price_1_day || 0).toFixed(0)}`}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
                  {results.length === 0 && (
                    <Button onClick={() => navigate('/produkte')} className="rounded-full bg-sky-600 hover:bg-sky-700 text-white">
                      Alle Produkte ansehen
                    </Button>
                  )}
                  <Button variant="outline" onClick={reset} className="rounded-full" data-testid="quiz-restart">
                    <RotateCcw className="h-4 w-4 mr-1" /> Quiz neu starten
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuizSection;
